import { type ComponentProps, memo, type ReactNode } from 'react';

import type { Rarity } from '@/shared/types/rarity';
import { cn } from '@/shared/utils/cn';

import { DuoCard } from './duoCard';

interface BaseDuoCardsPairProps extends ComponentProps<'div'> {
  leftRarity: Rarity;
  rightRarity: Rarity;
  leftRank: ReactNode;
  rightRank: ReactNode;
  leftImage: string;
  rightImage: string;
  leftName: string;
  rightName: string;
  leftIsPremium?: boolean;
  rightIsPremium?: boolean;
}

interface SmallDuoCardsPairProps extends BaseDuoCardsPairProps {
  size?: 'small';
  leftAgentId?: never;
  rightAgentId?: never;
}

interface LargeDuoCardsPairProps extends BaseDuoCardsPairProps {
  size: 'large';
  leftAgentId: string;
  rightAgentId: string;
}

export type DuoCardsPairProps = SmallDuoCardsPairProps | LargeDuoCardsPairProps;

export const DuoCardsPair = memo((props: DuoCardsPairProps) => {
  const {
    leftRarity,
    rightRarity,
    leftRank,
    rightRank,
    className,
    leftName,
    rightName,
    leftImage,
    rightImage,
    leftIsPremium,
    rightIsPremium,
    ...rest
  } = props;

  if (props.size === 'large') {
    return (
      <div
        data-slot="duo-cards-pair"
        {...rest}
        className={cn('flex', className)}
      >
        <DuoCard
          side="left"
          size="large"
          name={leftName}
          image={leftImage}
          rarity={leftRarity}
          rank={leftRank}
          agentId={props.leftAgentId}
          isPremium={leftIsPremium}
        />
        <DuoCard
          side="right"
          size="large"
          name={rightName}
          image={rightImage}
          rarity={rightRarity}
          rank={rightRank}
          agentId={props.rightAgentId}
          isPremium={rightIsPremium}
        />
      </div>
    );
  }

  return (
    <div data-slot="duo-cards-pair" {...rest} className={cn('flex', className)}>
      <DuoCard
        side="left"
        size="small"
        name={leftName}
        image={leftImage}
        rarity={leftRarity}
        rank={leftRank}
        isPremium={leftIsPremium}
      />
      <DuoCard
        side="right"
        size="small"
        name={rightName}
        image={rightImage}
        rarity={rightRarity}
        rank={rightRank}
        isPremium={rightIsPremium}
      />
    </div>
  );
});
